import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Instagram, Mail, Phone, Share2 } from "lucide-react";
import {
    LOGO_URL,
    WHATSAPP_URL,
    PHONE_DISPLAY,
    EMAIL,
    INSTAGRAM,
    INSTAGRAM_URL,
    SHARE_URL,
} from "../lib/constants";
import EmailCapture from "./EmailCapture";

// Hide site footer on dedicated funnel/print pages
const HIDDEN_FOOTER_ROUTES = [
    "/sleep",
    "/plan",
    "/session",
    "/investors",
    "/dapperdan",
    "/thank-you",
    "/live",
    "/watch",
    "/brochure",
    "/brochure-corporate",
    "/brochure-gym",
    "/brochure-realestate",
    "/brochure-athletes",
    "/resume",
    "/cover-letter",
    "/application",
    "/cover-letter-portable",
    "/application-portable",
    "/cover-letter-wynn",
    "/application-wynn",
    "/application-wynn-stage",
    "/application-wynn-automation",
    "/application-tether",
    "/reel",
    "/sunrise-pitch",
];

export const Footer = () => {
    const { pathname } = useLocation();
    if (HIDDEN_FOOTER_ROUTES.includes(pathname)) return null;

    return (
        <footer
            data-testid="site-footer"
            style={{
                borderTop: "1px solid rgba(201,162,39,0.15)",
                padding: "72px 32px 40px",
                backgroundColor: "#0a0a0a",
                position: "relative",
                zIndex: 1,
            }}
        >
            {/* EMAIL CAPTURE — sitewide */}
            <div
                data-testid="footer-email-capture-section"
                style={{
                    maxWidth: 1400,
                    margin: "0 auto 56px",
                    paddingBottom: 56,
                    borderBottom: "1px solid rgba(201,162,39,0.12)",
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                    gap: 48,
                    alignItems: "start",
                }}
            >
                <div>
                    <p
                        className="eyebrow"
                        style={{
                            marginBottom: 14,
                            color: "#c9a227",
                            fontSize: 11,
                            letterSpacing: "0.3em",
                        }}
                    >
                        The Sleep Track — Free
                    </p>
                    <h3
                        className="font-serif"
                        style={{
                            color: "#fff",
                            fontSize: "clamp(24px, 3vw, 36px)",
                            fontWeight: 400,
                            lineHeight: 1.15,
                            letterSpacing: "-0.02em",
                            margin: "0 0 14px",
                            maxWidth: 460,
                        }}
                    >
                        Sleep better{" "}
                        <span style={{ color: "#c9a227" }}>tonight.</span>
                    </h3>
                    <p
                        style={{
                            color: "#888",
                            fontSize: 14,
                            lineHeight: 1.7,
                            margin: 0,
                            maxWidth: 440,
                            fontWeight: 300,
                        }}
                    >
                        An 8-hour sound journey by Tatva K. Sent once. No
                        sequences. No upsells.
                    </p>
                </div>
                <div style={{ justifySelf: "start", width: "100%" }}>
                    <EmailCapture
                        variant="footer"
                        source="footer"
                        headline=""
                        subline=""
                        cta="Send It"
                        testIdPrefix="footer-email"
                    />
                </div>
            </div>
            <div
                style={{
                    maxWidth: 1400,
                    margin: "0 auto",
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                    gap: 48,
                    marginBottom: 56,
                }}
            >
                <div>
                    <Link to="/" data-testid="footer-logo-link">
                        <img
                            src={LOGO_URL}
                            alt="The Reset Sanctuary"
                            style={{ height: 88, marginBottom: 20 }}
                        />
                    </Link>
                    <p
                        style={{
                            color: "#888888",
                            fontSize: 14,
                            lineHeight: 1.7,
                            maxWidth: 320,
                            margin: 0,
                        }}
                    >
                        The Reset Sanctuary. Recovery Through Precision
                        Sound Recovery.
                    </p>
                </div>

                <div>
                    <h4
                        className="eyebrow"
                        style={{ marginBottom: 18, color: "#c9a227" }}
                    >
                        Locations
                    </h4>
                    <p
                        style={{
                            color: "#ffffff",
                            fontSize: 14,
                            marginBottom: 8,
                        }}
                    >
                        Al Hamra, Ras Al Khaimah
                    </p>
                    <p style={{ color: "#ffffff", fontSize: 14, margin: 0 }}>
                        Al Furjan, Dubai
                    </p>
                    <p
                        style={{
                            color: "#888888",
                            fontSize: 13,
                            marginTop: 8,
                        }}
                    >
                        In-villa sessions available UAE-wide.
                    </p>
                </div>

                <div>
                    <h4
                        className="eyebrow"
                        style={{ marginBottom: 18, color: "#c9a227" }}
                    >
                        Contact
                    </h4>
                    <a
                        href={WHATSAPP_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="footer-whatsapp-link"
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            color: "#ffffff",
                            textDecoration: "none",
                            fontSize: 14,
                            marginBottom: 12,
                        }}
                    >
                        <Phone size={14} color="#c9a227" />
                        {PHONE_DISPLAY}
                    </a>
                    <a
                        href={`mailto:${EMAIL}`}
                        data-testid="footer-email-link"
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            color: "#ffffff",
                            textDecoration: "none",
                            fontSize: 13,
                            marginBottom: 12,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                        }}
                        title={EMAIL}
                    >
                        <Mail size={14} color="#c9a227" style={{ flexShrink: 0 }} />
                        {EMAIL}
                    </a>
                    <a
                        href={INSTAGRAM_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="footer-instagram-link"
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            color: "#ffffff",
                            textDecoration: "none",
                            fontSize: 14,
                        }}
                    >
                        <Instagram size={14} color="#c9a227" />
                        {INSTAGRAM}
                    </a>
                </div>

                <div>
                    <h4
                        className="eyebrow"
                        style={{ marginBottom: 18, color: "#c9a227" }}
                    >
                        Navigate
                    </h4>
                    {[
                        { to: "/", label: "Home" },
                        { to: "/about", label: "About" },
                        { to: "/science", label: "The Science Behind It" },
                        { to: "/book", label: "Book" },
                        { to: "/contact", label: "Contact" },
                        { to: "/privacy", label: "Privacy Policy" },
                        { to: "/refund", label: "Refund Policy" },
                    ].map((item) => (
                        <Link
                            key={item.to}
                            to={item.to}
                            data-testid={`footer-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                            style={{
                                display: "block",
                                color: "#ffffff",
                                textDecoration: "none",
                                fontSize: 14,
                                marginBottom: 10,
                                letterSpacing: "0.05em",
                            }}
                        >
                            {item.label}
                        </Link>
                    ))}
                </div>
            </div>

            <div className="hairline" />

            <div
                style={{
                    maxWidth: 1400,
                    margin: "0 auto",
                    paddingTop: 28,
                    paddingBottom: 6,
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "center",
                    gap: 10,
                    alignItems: "center",
                }}
            >
                <p
                    style={{
                        color: "#888",
                        fontSize: 12,
                        letterSpacing: "0.18em",
                        textTransform: "uppercase",
                        margin: 0,
                    }}
                >
                    Referrals appreciated —
                </p>
                <a
                    href={SHARE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="footer-share-link"
                    style={{
                        color: "#c9a227",
                        fontSize: 12,
                        letterSpacing: "0.18em",
                        textTransform: "uppercase",
                        textDecoration: "none",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 8,
                        borderBottom: "1px solid transparent",
                        transition: "border-color 0.3s ease",
                        paddingBottom: 2,
                    }}
                    onMouseEnter={(e) =>
                        (e.currentTarget.style.borderColor = "#c9a227")
                    }
                    onMouseLeave={(e) =>
                        (e.currentTarget.style.borderColor = "transparent")
                    }
                >
                    <Share2 size={14} />
                    Share via WhatsApp
                </a>
            </div>

            <div
                style={{
                    maxWidth: 1400,
                    margin: "0 auto",
                    paddingTop: 16,
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "space-between",
                    gap: 16,
                }}
            >
                <p
                    style={{
                        color: "#666",
                        fontSize: 12,
                        letterSpacing: "0.15em",
                        textTransform: "uppercase",
                        margin: 0,
                    }}
                >
                    © 2026 Tatva K
                </p>
                <p
                    style={{
                        color: "#666",
                        fontSize: 12,
                        letterSpacing: "0.15em",
                        textTransform: "uppercase",
                        margin: 0,
                    }}
                >
                    Recovery by Tatva K
                </p>
            </div>

            {/* LEGAL DISCLAIMER */}
            <div
                data-testid="legal-disclaimer"
                style={{
                    maxWidth: 1400,
                    margin: "32px auto 0",
                    paddingTop: 24,
                    borderTop: "1px solid rgba(201,162,39,0.1)",
                }}
            >
                <p
                    style={{
                        color: "#666",
                        fontSize: 11,
                        lineHeight: 1.7,
                        letterSpacing: "0.02em",
                        textAlign: "center",
                        margin: 0,
                        maxWidth: 900,
                        marginLeft: "auto",
                        marginRight: "auto",
                    }}
                >
                    The Reset Sanctuary offers wellness and recovery sessions
                    through precision sound and frequency work. We do not
                    diagnose, treat, or cure medical conditions. Our services
                    are complementary wellness practices, not medical care.
                    Please consult a licensed healthcare provider for medical
                    concerns.
                </p>
            </div>
        </footer>
    );
};

export default Footer;
